import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { HelpCircle, Shield } from "lucide-react";
import { filterNameInput, handlePasteWithoutAccents } from "utils/inputFilters";
import {
  validatePhoneNotAdvisor,
  validatePhoneNumber,
} from "utils/validators/generalValidators";

import { EmailInput, EmailInputRef } from "js/components/ui/email-input";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import { RadioGroup, RadioGroupItem } from "js/components/ui/radio-group";
import { Switch } from "js/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "js/components/ui/tooltip";

import { StepComponentProps } from "../types";
import { CountryCodeSelect } from "./CountryCodeSelect";

interface Step3Props extends StepComponentProps {
  highlightGuarantorCredit?: boolean;
  externalEmailError?: string;
  clearExternalEmailError?: () => void;
  externalPhoneError?: string;
  clearExternalPhoneError?: () => void;
}

export const Step3GuarantorConfiguration = ({
  formData,
  updateFormData,
  isAnimating,
  highlightGuarantorCredit,
  externalEmailError = "",
  clearExternalEmailError,
  externalPhoneError = "",
  clearExternalPhoneError,
}: Step3Props) => {
  const creditQuestionRef = useRef<HTMLDivElement | null>(null);
  const emailInputRef = useRef<EmailInputRef>(null);
  const [phoneError, setPhoneError] = useState<string>("");

  const { userData } = useSelector((state: any) => state.entities.user);

  const displayPhoneError = externalPhoneError || phoneError;

  useEffect(() => {
    if (highlightGuarantorCredit && creditQuestionRef.current) {
      creditQuestionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlightGuarantorCredit]);

  const handlePhoneBlur = async () => {
    const phone = formData.guarantorPhoneNumber;
    if (phone && phone.trim() !== "") {
      try {
        await validatePhoneNumber(null, phone);
        await validatePhoneNotAdvisor(userData, phone);
        setPhoneError("");
      } catch (error: any) {
        setPhoneError(error.message);
      }
    } else {
      setPhoneError("");
    }
  };
  return (
    <TooltipProvider>
      <div
        className={`space-y-8 transition-all duration-700 ease-out ${isAnimating ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}
      >
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4">
            <Shield className="w-5 h-5 text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">
              Configuración de Garantías
            </h3>
          </div>
          <div className="flex items-center justify-between py-4 px-6 bg-gray-50 rounded-lg border transition-all duration-200 ease-in-out hover:shadow-sm">
            <span className="text-sm font-medium text-gray-700">
              ¿Requieres fiador u obligado solidario?
            </span>
            <Switch
              checked={formData.requiresGuarantor}
              onCheckedChange={(checked) => {
                updateFormData("requiresGuarantor", checked);
                if (!checked) {
                  updateFormData("guarantorProofOfIncome", false);
                }
              }}
              className="data-[state=checked]:bg-gray-900"
            />
          </div>
          {formData.requiresGuarantor && (
            <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-500 ease-out border border-gray-200 rounded-lg p-6">
              <h4 className="text-base font-semibold text-gray-900">
                Datos del Fiador/Obligado Solidario
              </h4>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Tipo de persona
                </Label>
                <RadioGroup
                  value={formData.guarantorType}
                  onValueChange={(value) =>
                    updateFormData("guarantorType", value)
                  }
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  <label
                    htmlFor="guarantor-fisica"
                    className={`relative flex cursor-pointer rounded-md border p-3 transition-all duration-200 ease-in-out hover:bg-gray-50 hover:shadow-sm hover:scale-[1.01] ${
                      formData.guarantorType === "fisica"
                        ? "border-blue-500 bg-blue-50/50 shadow-sm"
                        : "border-gray-300"
                    }`}
                  >
                    <RadioGroupItem
                      value="fisica"
                      id="guarantor-fisica"
                      className="mt-0.5"
                    />
                    <div className="ml-2.5">
                      <div className="text-sm font-medium text-gray-900">
                        Persona Física
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Individuos con RFC e identificación
                      </div>
                    </div>
                  </label>
                  <label
                    htmlFor="guarantor-moral"
                    className={`relative flex cursor-pointer rounded-md border p-3 transition-all duration-200 ease-in-out hover:bg-gray-50 hover:shadow-sm hover:scale-[1.01] ${
                      formData.guarantorType === "moral"
                        ? "border-blue-500 bg-blue-50/50 shadow-sm"
                        : "border-gray-300"
                    }`}
                  >
                    <RadioGroupItem
                      value="moral"
                      id="guarantor-moral"
                      className="mt-0.5"
                    />
                    <div className="ml-2.5">
                      <div className="text-sm font-medium text-gray-900">
                        Persona Moral
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Empresas o entidades legales
                      </div>
                    </div>
                  </label>
                </RadioGroup>
              </div>
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500 ease-out fill-mode-both"
                style={{ animationDelay: "150ms" }}
              >
                <div className="py-4 px-6 border border-gray-200 rounded-lg transition-all duration-200 ease-in-out hover:shadow-sm hover:border-gray-300">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-gray-700">
                      ¿Se pedirá propiedad en garantía?
                    </span>
                  </div>
                  <RadioGroup
                    value={formData.propertyAsGuarantee ? "yes" : "no"}
                    onValueChange={(value) =>
                      updateFormData("propertyAsGuarantee", value === "yes")
                    }
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="property-yes" />
                      <Label
                        htmlFor="property-yes"
                        className="text-sm text-gray-700"
                      >
                        Sí
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="property-no" />
                      <Label
                        htmlFor="property-no"
                        className="text-sm text-gray-700"
                      >
                        No
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                <div
                  ref={creditQuestionRef}
                  className={`py-4 px-6 border border-gray-200 rounded-lg transition-all duration-200 ease-in-out hover:shadow-sm hover:border-gray-300 ${
                    highlightGuarantorCredit
                      ? "ring-2 ring-green-500 pulse-highlight"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      Verificación Integral del Fiador
                    </span>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm p-3">
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <p className="font-semibold">
                              Costo Adicional: $299 MXN (IVA incluido) (PF).
                            </p>
                            <p className="font-semibold">
                              Costo Adicional: $499 MXN (IVA incluido) (PM).
                            </p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-medium">
                              Este servicio añade:
                            </p>
                            <ul className="text-sm space-y-1">
                              <li>
                                • Investigación de antecedentes legales a nivel
                                nacional.
                              </li>
                              <li>
                                • Consulta de historial en Buró de Crédito.
                              </li>
                              <li>
                                • Verificación en listas negras (SAT, FGR,
                                etc.).
                              </li>
                              <li>
                                • Web Check - Análisis de riesgo digital del
                                fiador.
                              </li>
                            </ul>
                          </div>
                          <p className="text-sm font-medium">
                            Es la inversión clave para asegurar al 100% la
                            solidez de tu garantía.
                          </p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Añade el análisis legal y crediticio del fiador para un
                    blindaje completo.
                  </p>
                  <RadioGroup
                    value={formData.creditInvestigation ? "yes" : "no"}
                    onValueChange={(value) => {
                      updateFormData("creditInvestigation", value === "yes");
                      if (value !== "yes") {
                        updateFormData("guarantorProofOfIncome", false);
                      }
                    }}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="credit-yes" />
                      <Label
                        htmlFor="credit-yes"
                        className="text-sm text-gray-700"
                      >
                        Sí
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="credit-no" />
                      <Label
                        htmlFor="credit-no"
                        className="text-sm text-gray-700"
                      >
                        No
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {formData.creditInvestigation && (
                <div
                  className="py-4 px-6 border border-gray-200 rounded-lg transition-all duration-200 ease-in-out hover:shadow-sm hover:border-gray-300 animate-in fade-in slide-in-from-top-2 duration-500 ease-out"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-gray-700">
                      ¿Deseas solicitar comprobantes de ingresos al
                      fiador/obligado solidario?
                    </span>
                  </div>
                  <RadioGroup
                    value={formData.guarantorProofOfIncome ? "yes" : "no"}
                    onValueChange={(value) =>
                      updateFormData("guarantorProofOfIncome", value === "yes")
                    }
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="proof-income-yes" />
                      <Label
                        htmlFor="proof-income-yes"
                        className="text-sm text-gray-700"
                      >
                        Sí
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="proof-income-no" />
                      <Label
                        htmlFor="proof-income-no"
                        className="text-sm text-gray-700"
                      >
                        No
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {formData.creditInvestigation && (
                <div className="space-y-6 animate-in fade-in slide-in-from-top-1 duration-600 ease-out border-t border-gray-200 pt-6 mt-6">
                  <h5 className="text-base font-semibold text-gray-900">
                    Datos del{" "}
                    {formData.guarantorType === "moral"
                      ? "Representante Legal"
                      : "Fiador/Obligado Solidario"}
                  </h5>

                  {/* Personal Information Section */}
                  <div
                    className="space-y-4 animate-in fade-in slide-in-from-left-2 duration-400 ease-out fill-mode-both"
                    style={{ animationDelay: "100ms" }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label
                          htmlFor="guarantorName"
                          className="text-sm font-medium text-gray-700 mb-2 block"
                        >
                          Nombre
                          {formData.guarantorType === "moral"
                            ? " del Representante Legal"
                            : ""}{" "}
                          *
                        </Label>
                        <Input
                          id="guarantorName"
                          type="text"
                          placeholder="Ingresa el nombre"
                          value={formData.guarantorName || ""}
                          onChange={(e) => {
                            const filtered = filterNameInput(e.target.value);
                            updateFormData(
                              "guarantorName",
                              filtered.toUpperCase(),
                            );
                          }}
                          onPaste={(e) => {
                            handlePasteWithoutAccents(
                              e,
                              filterNameInput,
                              (value) => {
                                updateFormData(
                                  "guarantorName",
                                  value.toUpperCase(),
                                );
                              },
                            );
                          }}
                          className="h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor="guarantorFirstSurname"
                          className="text-sm font-medium text-gray-700 mb-2 block"
                        >
                          Apellido Paterno
                          {formData.guarantorType === "moral"
                            ? " del Representante Legal"
                            : ""}{" "}
                          *
                        </Label>
                        <Input
                          id="guarantorFirstSurname"
                          type="text"
                          placeholder="Ingresa el apellido paterno"
                          value={formData.guarantorFirstSurname || ""}
                          onChange={(e) => {
                            const filtered = filterNameInput(e.target.value);
                            updateFormData(
                              "guarantorFirstSurname",
                              filtered.toUpperCase(),
                            );
                          }}
                          onPaste={(e) => {
                            handlePasteWithoutAccents(
                              e,
                              filterNameInput,
                              (value) => {
                                updateFormData(
                                  "guarantorFirstSurname",
                                  value.toUpperCase(),
                                );
                              },
                            );
                          }}
                          className="h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor="guarantorSecondSurname"
                          className="text-sm font-medium text-gray-700 mb-2 block"
                        >
                          Apellido Materno
                          {formData.guarantorType === "moral"
                            ? " del Representante Legal"
                            : ""}
                        </Label>
                        <Input
                          id="guarantorSecondSurname"
                          type="text"
                          placeholder="Ingresa el apellido materno"
                          value={formData.guarantorSecondSurname || ""}
                          onChange={(e) => {
                            const filtered = filterNameInput(e.target.value);
                            updateFormData(
                              "guarantorSecondSurname",
                              filtered.toUpperCase(),
                            );
                          }}
                          onPaste={(e) => {
                            handlePasteWithoutAccents(
                              e,
                              filterNameInput,
                              (value) => {
                                updateFormData(
                                  "guarantorSecondSurname",
                                  value.toUpperCase(),
                                );
                              },
                            );
                          }}
                          className="h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information Section */}
                  <div
                    className="space-y-4 animate-in fade-in slide-in-from-left-2 duration-400 ease-out fill-mode-both"
                    style={{ animationDelay: "200ms" }}
                  >
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2">
                        <EmailInput
                          ref={emailInputRef}
                          value={formData.guarantorEmail || ""}
                          onChange={(value) => {
                            updateFormData("guarantorEmail", value);
                            if (externalEmailError && clearExternalEmailError) {
                              clearExternalEmailError();
                            }
                          }}
                          label={`Correo Electrónico${formData.guarantorType === "moral" ? " del Representante Legal" : ""}`}
                          placeholder="correo@ejemplo.com"
                          required
                          userData={userData}
                          showIcon
                          showStatus
                          className="h-11"
                          error={externalEmailError}
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor="guarantorPhoneNumber"
                          className="text-sm font-medium text-gray-700 mb-2 block"
                        >
                          Teléfono
                          {formData.guarantorType === "moral"
                            ? " del Representante Legal"
                            : ""}{" "}
                          *
                        </Label>
                        <div className="flex gap-2">
                          <div className="w-32">
                            <CountryCodeSelect
                              value={formData.guarantorDialCode || "+52"}
                              onValueChange={(value) =>
                                updateFormData("guarantorDialCode", value)
                              }
                            />
                          </div>
                          <Input
                            id="guarantorPhoneNumber"
                            type="tel"
                            placeholder="1234567890"
                            value={formData.guarantorPhoneNumber || ""}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              if (value.length <= 10) {
                                updateFormData("guarantorPhoneNumber", value);
                              }
                              // Clear both local and external errors when user starts typing
                              if (phoneError) {
                                setPhoneError("");
                              }
                              if (
                                externalPhoneError &&
                                clearExternalPhoneError
                              ) {
                                clearExternalPhoneError();
                              }
                            }}
                            onBlur={handlePhoneBlur}
                            className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 flex-1 ${displayPhoneError ? "border-red-500" : ""}`}
                            maxLength={10}
                          />
                        </div>
                        {displayPhoneError && (
                          <span className="text-red-500 text-xs mt-1 block">
                            {displayPhoneError}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
